import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

function ProfilePage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sideOpen, setSideOpen] = useState(true);
  const [saveMsg, setSaveMsg] = useState("");
  const [formData, setFormData] = useState({
    full_name:"", email:"", phone_number:"", date_of_birth:"", gender:"",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("access");
    if (!token) { navigate("/login"); return; }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get("me/");
      setUser(res.data);
      setFormData({
        full_name: res.data.full_name || "",
        email: res.data.email || "",
        phone_number: res.data.phone_number || "",
        date_of_birth: res.data.date_of_birth || "",
        gender: res.data.gender || "",
      });
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch("me/", formData);
      setSaveMsg("Profile updated successfully.");
      setEditing(false);
      fetchProfile();
      setTimeout(() => setSaveMsg(""), 3000);
    } catch {
      setSaveMsg("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    navigate("/login");
  };

  const mkInitials = (name = "") => name.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase() || "U";
  const genderLabel = (g) => g === "M" ? "Male" : g === "F" ? "Female" : g === "O" ? "Prefer not to say" : "—";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" }) : "—";
  const W = sideOpen ? "210px" : "50px";

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", fontFamily:"Inter,sans-serif", color:"#6b7280", fontSize:13 }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:32, height:32, border:"2px solid #e5e7eb", borderTopColor:"#00897B", borderRadius:"50%", animation:"spin .7s linear infinite", margin:"0 auto 12px" }}></div>
        Loading profile…
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', sans-serif; background: #f1f5f9; color: #111; font-size: 13px; -webkit-font-smoothing: antialiased; }
        button { font-family: inherit; cursor: pointer; }
        .app { display: flex; min-height: 100vh; }

        .sb { width:${W}; background:#00897B; display:flex; flex-direction:column; position:fixed; top:0; left:0; bottom:0; z-index:200; transition:width .2s; overflow:hidden; }
        .sb-logo { height:56px; display:flex; align-items:center; padding:0 13px; gap:9px; border-bottom:1px solid rgba(255,255,255,0.1); flex-shrink:0; }
        .sb-logo-box { width:28px; height:28px; border-radius:7px; background:#fff; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .sb-logo-box svg { width:15px; height:15px; stroke:#00897B; fill:none; stroke-width:2.2; stroke-linecap:round; stroke-linejoin:round; }
        .sb-logo-name { font-size:13px; font-weight:700; color:#fff; white-space:nowrap; opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .sb-logo-sub { font-size:9.5px; color:rgba(255,255,255,0.45); white-space:nowrap; opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .sb-nav { flex:1; padding:10px 7px; overflow-y:auto; scrollbar-width:none; }
        .sb-grp { font-size:9.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:rgba(255,255,255,0.3); padding:8px 8px 3px; white-space:nowrap; opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .nb { width:100%; display:flex; align-items:center; gap:8px; padding:8px 9px; border-radius:7px; border:none; background:none; color:rgba(255,255,255,0.5); font-size:12.5px; font-weight:500; text-align:left; white-space:nowrap; margin-bottom:1px; transition:background .1s, color .1s; }
        .nb:hover { background:rgba(255,255,255,0.1); color:rgba(255,255,255,0.9); }
        .nb.on { background:rgba(255,255,255,0.15); color:#fff; font-weight:600; }
        .nav-ic { flex-shrink:0; width:15px; height:15px; display:flex; align-items:center; justify-content:center; }
        .nav-lbl { opacity:${sideOpen?1:0}; transition:opacity .15s; white-space:nowrap; }
        .sb-foot { border-top:1px solid rgba(255,255,255,0.1); padding:8px 7px; flex-shrink:0; }
        .sb-user { display:flex; align-items:center; gap:8px; padding:7px 9px; }
        .sb-av-ring { width:28px; height:28px; border-radius:50%; background:rgba(255,255,255,0.18); display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#fff; flex-shrink:0; }
        .sb-uinfo { opacity:${sideOpen?1:0}; transition:opacity .15s; overflow:hidden; }
        .sb-uname { font-size:12px; font-weight:600; color:#fff; white-space:nowrap; }
        .sb-urole { font-size:10px; color:rgba(255,255,255,0.35); white-space:nowrap; }
        .sb-out { width:100%; display:flex; align-items:center; gap:8px; padding:7px 9px; border-radius:7px; border:none; background:none; color:rgba(255,255,255,0.3); font-size:12px; margin-top:2px; transition:background .1s, color .1s; }
        .sb-out:hover { background:rgba(239,68,68,0.15); color:#fca5a5; }
        .sb-out-lbl { opacity:${sideOpen?1:0}; transition:opacity .15s; }

        .main { margin-left:${W}; flex:1; display:flex; flex-direction:column; transition:margin-left .2s; min-width:0; }
        .topbar { height:56px; background:#fff; border-bottom:1px solid #e5e7eb; display:flex; align-items:center; justify-content:space-between; padding:0 18px; position:sticky; top:0; z-index:100; flex-shrink:0; }
        .tb-l { display:flex; align-items:center; gap:10px; }
        .tb-toggle { width:30px; height:30px; border-radius:6px; border:1px solid #e5e7eb; background:none; display:flex; align-items:center; justify-content:center; color:#6b7280; }
        .tb-toggle:hover { background:#f3f4f6; }
        .tb-title { font-size:13.5px; font-weight:700; color:#0f172a; }
        .tb-sub { font-size:11px; color:#9ca3af; }
        .tb-r { display:flex; align-items:center; gap:7px; }
        .tb-badge { display:flex; align-items:center; gap:5px; padding:4px 10px; border-radius:20px; background:#E0F2F1; font-size:10.5px; font-weight:600; color:#00897B; }
        .tb-av { width:30px; height:30px; border-radius:50%; background:#00897B; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#fff; }

        .content { padding:16px; flex:1; }
        .two-col { display:grid; grid-template-columns:1fr 300px; gap:14px; align-items:start; }
        .card { background:#fff; border:1px solid #e5e7eb; border-radius:12px; overflow:hidden; }
        .card-head { padding:16px 18px; border-bottom:1px solid #f3f4f6; display:flex; align-items:center; justify-content:space-between; }
        .card-title { font-size:13.5px; font-weight:700; color:#0f172a; }
        .card-body { padding:18px; }

        .profile-banner { background:#00897B; padding:28px 18px; text-align:center; }
        .av-circle { width:64px; height:64px; border-radius:50%; background:rgba(255,255,255,0.18); border:3px solid rgba(255,255,255,0.3); display:flex; align-items:center; justify-content:center; font-size:22px; font-weight:700; color:#fff; margin:0 auto 12px; }
        .av-name { font-size:16px; font-weight:700; color:#fff; margin-bottom:3px; }
        .av-role { font-size:11px; color:rgba(255,255,255,0.55); }
        .av-id { display:inline-flex; align-items:center; gap:5px; margin-top:8px; background:rgba(255,255,255,0.12); border:1px solid rgba(255,255,255,0.2); border-radius:20px; padding:3px 10px; font-size:11px; color:rgba(255,255,255,0.75); font-weight:500; }

        .info-row { display:flex; align-items:center; justify-content:space-between; padding:11px 0; border-bottom:1px solid #f3f4f6; }
        .info-row:last-child { border-bottom:none; padding-bottom:0; }
        .info-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#9ca3af; }
        .info-value { font-size:13px; font-weight:500; color:#0f172a; text-align:right; }

        .field { margin-bottom:10px; }
        .field label { display:block; font-size:11px; font-weight:500; color:#475569; margin-bottom:5px; }
        .field input, .field select { width:100%; height:38px; padding:0 10px; font-size:12px; border:1px solid #e2e8f0; border-radius:8px; background:#f8fafc; color:#0f172a; outline:none; transition:border-color .15s, box-shadow .15s; appearance:none; }
        .field input:focus, .field select:focus { border-color:#00897B; box-shadow:0 0 0 3px rgba(0,137,123,0.1); background:#fff; }
        .field input:disabled { background:#f1f5f9; color:#9ca3af; }
        .field-hint { font-size:10.5px; color:#9ca3af; margin-top:3px; }
        .grid2 { display:grid; grid-template-columns:1fr 1fr; gap:10px; }

        .btn-edit { padding:6px 14px; border-radius:7px; border:1px solid #e5e7eb; background:#fff; font-size:12px; font-weight:600; color:#00897B; transition:background .1s; }
        .btn-edit:hover { background:#E0F2F1; }
        .btn-save { padding:6px 14px; border-radius:7px; border:none; background:#00897B; font-size:12px; font-weight:600; color:#fff; transition:background .1s; }
        .btn-save:hover { background:#00695C; }
        .btn-save:disabled { opacity:.7; cursor:not-allowed; }
        .btn-cancel { padding:6px 14px; border-radius:7px; border:1px solid #e5e7eb; background:#f8fafc; font-size:12px; font-weight:500; color:#64748b; transition:background .1s; }
        .btn-cancel:hover { background:#f1f5f9; }
        .btn-action { width:100%; padding:10px 14px; border-radius:8px; border:1px solid #e5e7eb; background:#fff; font-size:12.5px; font-weight:500; color:#374151; text-align:left; transition:background .1s; display:flex; align-items:center; gap:9px; margin-bottom:6px; }
        .btn-action:hover { background:#f8fafc; border-color:#d1d5db; }
        .btn-action.danger { color:#dc2626; border-color:#fecaca; }
        .btn-action.danger:hover { background:#fef2f2; }
        .btn-action svg { width:14px; height:14px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; flex-shrink:0; }
        .action-icon { width:28px; height:28px; border-radius:7px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

        .save-msg { padding:9px 12px; border-radius:8px; font-size:11.5px; font-weight:500; margin-bottom:12px; }
        .save-ok { background:#f0fdf4; border:1px solid #bbf7d0; color:#16a34a; }
        .save-err { background:#fef2f2; border:1px solid #fecaca; color:#dc2626; }

        .compliance-card { background:#E0F2F1; border:1px solid #80CBC4; border-radius:10px; padding:13px 15px; }
        .compliance-title { font-size:12px; font-weight:700; color:#00897B; margin-bottom:6px; display:flex; align-items:center; gap:6px; }
        .compliance-title svg { width:13px; height:13px; stroke:#00897B; fill:none; stroke-width:2; }
        .compliance-text { font-size:11px; color:#00695C; line-height:1.6; }
        .comp-badges { display:flex; gap:5px; margin-top:8px; flex-wrap:wrap; }
        .comp-badge { font-size:10px; font-weight:600; padding:2px 8px; border-radius:20px; background:rgba(0,137,123,0.12); color:#00897B; }

        .spinner { width:13px; height:13px; border:2px solid rgba(255,255,255,0.3); border-top-color:#fff; border-radius:50%; animation:spin .6s linear infinite; }
        @keyframes spin { to { transform:rotate(360deg); } }
        @media (max-width:900px) { .two-col { grid-template-columns:1fr; } }
        @media (max-width:600px) { .content { padding:10px; } .grid2 { grid-template-columns:1fr; } }
      `}</style>

      <div className="app">
        {/* SIDEBAR */}
        <aside className="sb">
          <div className="sb-logo">
            <div className="sb-logo-box">
              <img src="/micronlabs.png" alt="MicronLabs" style={{ width:"18px", height:"18px", objectFit:"contain" }}/>
            </div>
            <div>
              <div className="sb-logo-name">MicronLabs</div>
              <div className="sb-logo-sub">Laboratory Information System</div>
            </div>
          </div>
          <nav className="sb-nav">
            <div className="sb-grp">Main</div>
            {[
              { id:"dashboard", label:"Dashboard", icon:<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></> },
              { id:"requisitions", label:"E-Requisitions", icon:<><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h7v7"/></> },
              { id:"results", label:"Lab Results", icon:<><path d="M9 3h6M9 3v8l-4 9h14l-4-9V3"/></> },
              { id:"prescriptions", label:"Prescriptions", icon:<><path d="M10.5 20H4a2 2 0 01-2-2v-3.5a2 2 0 012-2h6.5"/><path d="M13.5 4H20a2 2 0 012 2v3.5a2 2 0 01-2 2h-6.5"/><path d="M10.5 20l9-16"/></> },
              { id:"records", label:"Health Records", icon:<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8"/></> },
            ].map(({ id, label, icon }) => (
              <button key={id} className="nb" onClick={() => navigate(`/${id}`)}>
                <span className="nav-ic"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">{icon}</svg></span>
                <span className="nav-lbl">{label}</span>
              </button>
            ))}
            <div className="sb-grp" style={{marginTop:8}}>Account</div>
            {[
              { id:"messages", label:"Messages", icon:<><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></> },
              { id:"billing", label:"Billing", icon:<><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></> },
              { id:"profile", label:"My Profile", icon:<><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></> },
            ].map(({ id, label, icon }) => (
              <button key={id} className={`nb ${id==="profile"?"on":""}`} onClick={() => navigate(`/${id}`)}>
                <span className="nav-ic"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">{icon}</svg></span>
                <span className="nav-lbl">{label}</span>
              </button>
            ))}
          </nav>
          <div className="sb-foot">
            <div className="sb-user">
              <div className="sb-av-ring">{mkInitials(user?.full_name)}</div>
              <div className="sb-uinfo">
                <div className="sb-uname">{user?.full_name || "My Account"}</div>
                <div className="sb-urole">{user?.role || "patient"}</div>
              </div>
            </div>
            <button className="sb-out" onClick={handleLogout}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
              <span className="sb-out-lbl">Sign out</span>
            </button>
          </div>
        </aside>

        {/* MAIN */}
        <div className="main">
          <header className="topbar">
            <div className="tb-l">
              <button className="tb-toggle" onClick={() => setSideOpen(o => !o)}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
              </button>
              <div>
                <div className="tb-title">My Profile</div>
                <div className="tb-sub">{new Date().toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric" })}</div>
              </div>
            </div>
            <div className="tb-r">
              <div className="tb-badge">
                <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/></svg>
                HIPAA secured
              </div>
              <div className="tb-av">{mkInitials(user?.full_name)}</div>
            </div>
          </header>

          <div className="content">
            {saveMsg && (
              <div className={`save-msg ${saveMsg.includes("success") ? "save-ok" : "save-err"}`}>{saveMsg}</div>
            )}

            <div className="two-col">
              <div className="card">
                <div className="profile-banner">
                  <div className="av-circle">{mkInitials(user?.full_name)}</div>
                  <div className="av-name">{user?.full_name || "—"}</div>
                  <div className="av-role" style={{textTransform:"capitalize"}}>{user?.role || "Patient"}</div>
                  <div className="av-id">
                    <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                    ID #{user?.id || "—"}
                  </div>
                </div>

                <div className="card-head">
                  <span className="card-title">Personal information</span>
                  {!editing ? (
                    <button className="btn-edit" onClick={() => setEditing(true)}>Edit profile</button>
                  ) : (
                    <div style={{display:"flex", gap:6}}>
                      <button className="btn-cancel" onClick={() => { setEditing(false); fetchProfile(); }}>Cancel</button>
                      <button className="btn-save" onClick={handleSave} disabled={saving}>
                        {saving ? <><span className="spinner"/> Saving…</> : "Save changes"}
                      </button>
                    </div>
                  )}
                </div>

                <div className="card-body">
                  {!editing ? (
                    <>
                      {[
                        { label:"Full name", value:user?.full_name || "—" },
                        { label:"Email address", value:user?.email || "—" },
                        { label:"Phone number", value:user?.phone_number || "—" },
                        { label:"Date of birth", value:fmtDate(user?.date_of_birth) },
                        { label:"Gender", value:genderLabel(user?.gender) },
                        { label:"Role", value:user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "—" },
                      ].map(({ label, value }) => (
                        <div key={label} className="info-row">
                          <span className="info-label">{label}</span>
                          <span className="info-value">{value}</span>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div className="field"><label>Full name</label><input type="text" name="full_name" value={formData.full_name} onChange={handleChange}/></div>
                      <div className="field">
                        <label>Email address</label>
                        <input type="email" name="email" value={formData.email} disabled/>
                        <div className="field-hint">Email address cannot be changed.</div>
                      </div>
                      <div className="field"><label>Phone number</label><input type="tel" name="phone_number" value={formData.phone_number} onChange={handleChange} placeholder="(555) 000-0000"/></div>
                      <div className="grid2">
                        <div className="field"><label>Date of birth</label><input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange}/></div>
                        <div className="field">
                          <label>Gender</label>
                          <select name="gender" value={formData.gender} onChange={handleChange}>
                            <option value="">Select gender</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                            <option value="O">Prefer not to say</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div style={{display:"flex", flexDirection:"column", gap:12}}>
                <div className="card">
                  <div className="card-head"><span className="card-title">Account settings</span></div>
                  <div className="card-body">
                    <button className="btn-action">
                      <div className="action-icon" style={{background:"#E0F2F1"}}>
                        <svg width="14" height="14" fill="none" stroke="#00897B" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                      </div>
                      Change password
                    </button>
                    <button className="btn-action">
                      <div className="action-icon" style={{background:"#f0fdf4"}}>
                        <svg width="14" height="14" fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/></svg>
                      </div>
                      Notification preferences
                    </button>
                    <button className="btn-action">
                      <div className="action-icon" style={{background:"#fefce8"}}>
                        <svg width="14" height="14" fill="none" stroke="#ca8a04" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/></svg>
                      </div>
                      Privacy settings
                    </button>
                    <button className="btn-action danger" onClick={handleLogout} style={{marginBottom:0}}>
                      <div className="action-icon" style={{background:"#fef2f2"}}>
                        <svg width="14" height="14" fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
                      </div>
                      Sign out
                    </button>
                  </div>
                </div>

                <div className="compliance-card">
                  <div className="compliance-title">
                    <svg viewBox="0 0 24 24"><path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/><path d="M9 12l2 2 4-4"/></svg>
                    Your data is protected
                  </div>
                  <div className="compliance-text">Your health information is safeguarded under HIPAA. MicronLabs uses 256-bit encryption and strict access controls to keep your records private.</div>
                  <div className="comp-badges">
                    <span className="comp-badge">HIPAA</span>
                    <span className="comp-badge">CLIA</span>
                    <span className="comp-badge">SOC 2</span>
                    <span className="comp-badge">256-bit SSL</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ProfilePage;